// Quick Estimate 3 — service-line voice.
//
// Tone: terse, professional, AI-confident. We surface what the AI
// did (because that's the value-add) and stay out of the way for
// everything else. Errors blame the system, never the advisor.
//
// QE3 has 4 steps now:
//   1. Vehicle — VIN + decode (reused from QE2)
//   2. Photos — capture + quality verdict (good / retake / failed)
//   3. Tags   — AI side labels surfaced for review + edit
//   4. Review — vehicle/photo recap, notes, customer, send

export const QE_VEHICLE_COPY = {
  eyebrow: "Step 1 of 4 · Vehicle",
}

export const QE_PHOTO_COPY = {
  eyebrow: "Step 2 of 4 · Photos",
  title: "Snap the damage",
  // Step is purely about quality now — no side talk. AI will tag
  // sides on the next step; here the only question is "did the shot
  // come out usable?".
  subtitle: "Any angle. The AI checks each shot for clarity.",
  emptyHint: "No photos yet — tap the shutter.",
  // Native picker offers both Camera and multi-select Library, so
  // copy stays plural-friendly ("Add photos" reads right whether the
  // user shoots one or picks ten).
  capture: "Add photos",
  captureMore: "Add more photos",
  countOne: "1 photo",
  countMany: (n: number) => `${n} photos`,
  // AI activity chip — the live "how the AI is reviewing your shots"
  // surface. Variants depend on whether anything is in-flight, how
  // many came back clean, and how many need attention.
  aiReady: "AI is ready",
  aiReviewing: (count: number) =>
    count === 1 ? "AI reviewing 1 photo…" : `AI reviewing ${count} photos…`,
  aiAllClean: (n: number) =>
    n === 1 ? "AI says 1 photo looks clean" : `AI says ${n} photos look clean`,
  aiSomeIssues: (clean: number, issues: number) => {
    const cleanPart = clean === 0 ? "" : `${clean} clean`
    const issuesPart =
      issues === 1 ? `1 needs another look` : `${issues} need another look`
    return [cleanPart, issuesPart].filter(Boolean).join(" · ")
  },
  evaluating: "Reviewing…",
  // Per-photo verdict labels (footer of QualityTile).
  verdictGood: "Looks clean",
  verdictRetake: "Retake",
  verdictFailed: "Couldn't review",
  // Action labels in the editor.
  retake: "Retake",
  retry: "Retry review",
  useAnyway: "Keep anyway",
  sendWithoutTag: "Use without AI tag",
  inspect: "Inspect",
  remove: "Remove",
  // Sticky CTA + back link at the bottom.
  continueDisabled: "Add at least 1 photo to continue",
  continue: "Review tags",
  back: "Back to vehicle",
  // Preflight messaging (shown briefly while we downscale).
  shrinking: "Shrinking…",
  // Add-more affordance on review step.
  addMore: "Add more",
}

export const QE_SIDES_COPY = {
  eyebrow: "Step 3 of 4 · Tags",
  title: "Confirm the angles",
  subtitle: "AI tagged each photo. Tap any to correct.",
  // Activity chip — same family as photos step but anchored to the
  // tagging summary instead of quality.
  aiTaggedNone: "Nothing tagged yet",
  aiTaggedSummary: (sides: string[]) => {
    if (sides.length === 0) return "No tags yet"
    if (sides.length === 1) return `Tagged: ${sides[0]}`
    if (sides.length === 2) return `Tagged: ${sides[0]} & ${sides[1]}`
    return `Tagged: ${sides.slice(0, -1).join(", ")} & ${sides.at(-1)}`
  },
  aiTaggedSummaryEdited: (sides: string[], edited: number) => {
    const base = QE_SIDES_COPY.aiTaggedSummary(sides)
    if (edited === 0) return base
    if (edited === 1) return `${base} · 1 corrected`
    return `${base} · ${edited} corrected`
  },
  // Per-photo footer label fallback (when not failed/untagged).
  edit: "Edit side",
  edited: "Edited",
  untagged: "Untagged",
  cantTag: "Couldn't tag",
  // Coverage hints — soft nudges, never blockers.
  coverageEyebrow: "Coverage",
  coverageHint: (missing: string[]) =>
    missing.length === 0
      ? "Looks comprehensive."
      : missing.length === 1
        ? `Consider adding a ${missing[0].toLowerCase()} shot.`
        : `Consider adding ${missing.length} more angles.`,
  // Affordances around the grid.
  addMore: "Add more photos",
  // CTA + back link.
  continue: "Review & send",
  continueDisabled: "Tag at least 1 photo to continue",
  back: "Back to photos",
}

export const QE_LIGHTBOX_COPY = {
  // Header label inside the lightbox.
  detectedAs: "Detected side",
  edit: "Change side",
  describeLabel: "Note for the shop (optional)",
  describePlaceholder: "Driver-side dent, paint cracked along the seam.",
  qualityHigh: "AI quality: high",
  qualityMedium: "AI quality: medium",
  qualityLow: "AI quality: low",
  close: "Close",
}

export const QE_SIDE_SHEET_COPY = {
  title: "Pick the right side",
  subtitle: "AI suggested one — pick whichever matches.",
  groups: {
    front: "Front",
    rear: "Rear",
    sides: "Sides",
    corners: "Corners",
    roof: "Roof",
  } as const,
  cancel: "Cancel",
}

export const QE_SEND_COPY = {
  eyebrow: "Step 4 of 4 · Send",
  // Send step's job is to collect the one thing the previous steps
  // didn't (the customer) and ship. Title leans into that — it's
  // not a "last review" because the user already reviewed photos
  // (step 2) and tags (step 3).
  title: "Last details",
  subtitle: "Add the customer and send.",
  vehicleLabel: "Vehicle",
  photosLabel: "Photos",
  noPhotos: "No photos attached.",
  blockNoPhotos:
    "Add at least 1 photo before sending — the AI needs them to generate line items.",
  recapEdit: "Edit",
  recapPhotoCount: (n: number) => (n === 1 ? "1 photo ready" : `${n} photos ready`),
  recapNoPhotos: "No photos yet",
  notesLabel: "Note for the shop (optional)",
  notesPlaceholder: "RO #, drop-off, anything the shop should know.",
  customerHeading: "Customer details",
  customerOptional: "Optional",
  customerNameLabel: "Name",
  customerPhoneLabel: "Phone",
  recentCustomerPrompt: "Use last customer",
  recentCustomerSub: "From your last estimate",
  recentCustomerCta: "Tap to use",
  submit: "Send for Estimate",
  submitting: "Sending…",
  back: "Back to tags",
  // Success takeover — recaps what was sent rather than saying
  // "queued" with no detail.
  successTitle: "Estimate queued",
  successSubtitleOne: "1 photo sent · the AI is generating line items",
  successSubtitleMany: (n: number) =>
    `${n} photos sent · the AI is generating line items`,
  successEta: "Should be ready in about a minute.",
  successPrimary: "View estimate",
  successSecondary: "Start another",
  failTitle: "Couldn't send.",
  failBody: "Try again — the photos and details are still here.",
  failPrimary: "Try again",
  // Partial-success surface — the estimate created but image
  // attach failed. The advisor can retry the attach later or
  // re-submit.
  partialAttached: (sent: number, total: number) =>
    `${sent} of ${total} photos attached. Open the estimate to retry the rest.`,
}

export const QE_WIZARD_COPY = {
  leaveLabel: "Leave for now",
  leaveConfirmTitle: "Leave this estimate?",
  leaveConfirmBody:
    "Your work is saved on this device until the tab closes. Open this URL again to pick up.",
  leaveConfirmContinue: "Stay here",
  leaveConfirmExit: "Leave",
  noTeamTitle: "Pick a team to start",
  noTeamBody:
    "QE3 needs a dealership or body shop selected before it can submit.",
  noTeamCta: "Open team picker",
}
